import { Component, EventEmitter, forwardRef, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatExpansionPanel } from '@angular/material/expansion';
import { MatPaginator} from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource} from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { IMobileDevice } from '@app/certificates/devices/devices';
import { ICertificateData, CertStatus, PackType, statusIcon } from '@app/certificates/manage/manage';
import { ManageService } from '@app/certificates/manage/manage.service';
import { ActionPacksReset } from '@app/certificates/packs/packs.reducer';
import { EmergencyService } from '@app/core/auth/component/emergency-login/emergency-login.service';
import { IConfirmData } from '@app/core/info/confirm/confirm';
import { ConfirmComponent } from '@app/core/info/confirm/confirm.component';
import { InfoService } from '@app/core/info/info.service';
import { IQrData } from '@app/core/info/qr/qr';
import { QrComponent } from '@app/core/info/qr/qr.component';
import { iconCodes } from '@app/shared/cqes-icon/cqes-icon.component';
import { MatTableHelper } from '@app/shared/helpers/table-filter.helper';
import { IStatusIcon } from '@app/shared/interfaces/common-interfaces';
import { ISpringResponseBody } from '@app/shared/interfaces/response-interfaces';
import { environment } from '@env/environment';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Subject, Observable, of } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CertificateDetailsComponent } from '../certificate-details/certificate-details.component';
import { ManageCertificateEffects } from '../manage.effects';
import { ActionDevicesDeleteSuccess } from './../../devices/devices.reducer';
import { ActionCertificatesGet, ActionCertificatesUpdateDevicesState, selectorCertificate } from './../manage.reducer';
import { CertificatesRoutingModule } from '@app/certificates/certificates-routing.module';

@Component({
  selector: 'cloud-manage-grid',
  templateUrl: './manage-grid.component.html',
  styleUrls: ['./manage-grid.component.scss', './manage-grid.component-theme.scss'],
})
export class ManageGridComponent implements OnInit, OnDestroy {

  @Input() isSubcomponent = false;

  @ViewChild(forwardRef(() => MatExpansionPanel)) panel: MatExpansionPanel;
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(forwardRef(() => MatSort)) sort: MatSort;

  @Output() onShowDevices = new EventEmitter<ICertificateData>();

  readonly displayedColumns = [
    'status',
    'alias',
    'serialNo',
    'owner',
    'validTo',
    'actions',
  ];

  readonly statusLables = [
    'all',
    'active',
    'inactive',
  ];

  private _confirmDialog: MatDialogRef<ConfirmComponent>;
  readonly dialogTexts = 'manageTexts.dialogTexts.';
  readonly resultTexts = 'manageTexts.resultTexts.';
  readonly dateFormat = environment.dateFormat;
  readonly icons = iconCodes;

  fullAlias: string;
  commonName: string;
  holder: string;
  certSerialNumber: string;

  certFilterStatus = 'all';
  dataSource: MatTableDataSource<ICertificateData>;
  unsubscribe$: Subject<void> = new Subject<void>();
  pendingCertsInProgress$: Subject<void> = new Subject<void>();
  lang: string;
  isEmergencyLogin: boolean;
  loading = true;
  emptyCertificates = false;
  resetFilter: string;
  selectedDevice: IMobileDevice;

  private readonly activeCertStatusFilter = (cert) => (
    cert.status === CertStatus.VALID
  )

  private readonly inactiveCertStatusFilter = (cert) => (
    cert.status === CertStatus.EXPIRED
    || cert.status === CertStatus.REVOKED
    || cert.status === CertStatus.HOLDED
    || cert.status === CertStatus.PENDING_HOLD
    || cert.status === CertStatus.PENDING_RESTORE
    || cert.status === CertStatus.WAITING_ACTIVATION
  )

  private readonly allCertStatusFilter = (cert) => (
    cert.status === CertStatus.VALID
    || cert.status === CertStatus.HOLDED
    || cert.status === CertStatus.PENDING_HOLD
    || cert.status === CertStatus.PENDING_RESTORE
    || cert.status === CertStatus.EXPIRED
    || cert.status === CertStatus.REVOKED
    || cert.status === CertStatus.WAITING_ACTIVATION
  )

  constructor(
    public info: InfoService,
    private translateService: TranslateService,
    private router: Router,
    private route: ActivatedRoute,
    private manageService: ManageService,
    public dialog: MatDialog,
    private store: Store<any>,
    private effects: ManageCertificateEffects,
    private emergency: EmergencyService,
    public tableHelper: MatTableHelper,
  ) {
    this.lang = this.translateService.currentLang;
    this.translateService.onLangChange.subscribe((e) => this.lang = e.lang);
  }

  /**
   * Тъй-като използваме store, а трябва да затворим прозореца, след извършване на операцията,
   * се регистрираме, като наблюдатели за ефекти от различните действия с удостоверенията
   */

  ngOnInit() {
    this.info.dismissAll();
    this.getData();
    this.setSubscriptions();
    if (!this.isSubcomponent) {
      this.setPullForPendingStatus();
    }
    this.isEmergencyLogin = this.emergency.getEmergencyState();
  }

  applyFilter(filterValue: string) {
    filterValue = filterValue.trim(); // Remove whitespace
    filterValue = filterValue.toLowerCase(); // Datasource defaults to lowercase matches
    this.dataSource.filter = filterValue;
    this.dataSource.filterPredicate = this.tableHelper.filterPredicateForCertificates;
    this.onShowDevices.emit(null);
  }

  onDownloadCert(cert: ICertificateData) {
    this.manageService.getCert(cert.certReqId)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (result) => {
          if (result.status !== 200) {
            this.info.error(result.message);
          } else {
            const fileName = 'certificate_' + cert.serialNo + '.cer';
            const blob = new Blob([result.data], { type: 'application/pkix-cert' });
            if (this.isIE()) {
              const nav = (window.navigator as any) 
              if(nav.msSaveOrOpenBlob){
                nav.msSaveOrOpenBlob(blob, fileName);
              }
            } else {
              const link = document.createElement('a');
              link.href = window.URL.createObjectURL(blob);
              link.download = fileName;
              document.body.appendChild(link);
              link.click();
            }
          }
        },
      );
  }

  isIE() {
    const match = navigator.userAgent.search(/(?:Edge|MSIE|Trident\/.*; rv:)/);
    let isIE = false;
    if (match !== -1) {
      isIE = true;
    }
    return isIE;
  }

  areOrdersHidden(){
    return environment.hideOrders;
  }

  onHold(cert: ICertificateData) {
    const question = !cert.alias ? 'holdQuestion' : 'holdQuestionAlias';
    this.openConfirmDialog(
      'holdTitle',
      question,
      () => {
        this.manageService.hold(cert.certReqId);
        this._confirmDialog.close();
      },
      cert
    );
  }

  onRestore(cert: ICertificateData) {
    const question = !cert.alias ? 'restoreQuestion' : 'restoreQuestionAlias';
    this.openConfirmDialog(
      'restoreTitle',
      question,
      () => {
        this.manageService.restore(cert.certReqId);
        this._confirmDialog.close();
      },
      cert,
    );
  }

  showDevices(cert: ICertificateData) {
    this.onShowDevices.emit(cert);
  }

  onAddDevice(cert: ICertificateData) {
    const regex = /\/([^\/]+)$/;
    let tmpQrCode;
    this.manageService.addDevice(cert.certReqId).subscribe(qrCodeResult => {
      tmpQrCode = qrCodeResult;      
      let clearQrCode: string | false;
      if(qrCodeResult) {
        let match = qrCodeResult.match(regex); 
        clearQrCode = match ? match[1] : false;
      } else {
        clearQrCode = false;
      }
      let qrCodeObservable: Observable<string | false> = of(tmpQrCode);
      this.dialog.open(
        QrComponent,
        {
          data: {
            icon: this.icons.smartphone,
            title: this.translateService.instant(this.dialogTexts + 'addDeviceTitle'),
            getQrAction: qrCodeObservable,
            cancelTxt: this.translateService.instant('common.cancel'),
            confirmAction: this.manageService.checkDeviceAdded(clearQrCode),
            QRDescription: this.translateService.instant(this.dialogTexts + 'addDeviceDescription'),
            timeoutTxt: this.translateService.instant(this.dialogTexts + 'addDeviceTimer'),
            timeout: 3000,
          } as IQrData,
        });    
    });    
    
  }

  buyPack(cert: ICertificateData) {
    this.store.dispatch(new ActionPacksReset());

    this.router.navigate(
      ['../packs'],
      {
        relativeTo: this.route
      }
    );
  }

  isInfinite(cert: ICertificateData): boolean {
    return cert.packType === PackType.INFINITE ? true : false;
  }

  isValid(status: CertStatus) {
    return status === CertStatus.VALID;
  }

  isHolded(status: CertStatus) {
    return status === CertStatus.HOLDED;
  }

  isPending(status: CertStatus) {
    return status === CertStatus.PENDING_HOLD || status === CertStatus.PENDING_RESTORE;
  }

  isPendingHold(status: CertStatus) {
    return status === CertStatus.PENDING_HOLD;
  }

  isExpired(status: CertStatus): boolean {
    return status === CertStatus.EXPIRED;
  }

  isRevoked(status: CertStatus): boolean {
    return status === CertStatus.REVOKED;
  }

  isLimitReach(cert: ICertificateData): boolean {
    return cert.deviceIds.length >= cert.limit;
  }

  statusIcon(status: CertStatus): IStatusIcon {
    return statusIcon[status];
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    this.pendingCertsInProgress$.next();
    this.pendingCertsInProgress$.complete();
  }

  setDataSource(certificates: ICertificateData[]) {
    certificates = certificates.slice().sort((a, b) => {
      return new Date(b.validTo).getTime() - new Date(a.validTo).getTime();
    }); // sort by validity date descending
    if (this.isSubcomponent && this.selectedDevice) {
      // filter by selected device
     certificates = certificates.filter(cert => this.selectedDevice.certificateReqIds.indexOf(cert.certReqId) > -1);
    }
    this.loading = false;
    this.emptyCertificates = Object.keys(certificates).length === 0;
    this.dataSource = new MatTableDataSource(certificates);
    this.dataSource.sort = this.sort;
    this.setPaginator();
  }

  /**
   * Set the paginator and sort after the view init since this component will
   * be able to query its view for the initialized paginator and sort.
   */

  private setSubscriptions() {
    this.store
      .select(selectorCertificate)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((cert: ICertificateData[]) => {
        if (cert) {
          const certs = cert.filter(this.getCertificateStatusFilter(this.certFilterStatus));
          this.setDataSource(certs);
        }
      });

    this.effects.operationFailed$
      .pipe(
        takeUntil(this.unsubscribe$),
      ).subscribe(
        (r) => {
          this.operationResult(r.payload.response.message, false, true);
        },
      );
    this.effects.deleteDevices$.pipe(takeUntil(this.unsubscribe$)).subscribe((event: ActionDevicesDeleteSuccess) => {
      this.updateNumberOfDevices(event.payload.devices, event.payload.certReqId);
    });
  }

  private setPullForPendingStatus() {
    this.store
      .select(selectorCertificate)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((cert: ICertificateData[]) => {
        if (cert) {
          const pendingCertReqId = cert.filter((currentCert) =>
            currentCert.status === CertStatus.PENDING_HOLD || currentCert.status === CertStatus.PENDING_RESTORE
          );
          this.pendingCertsInProgress$.complete();
          this.pendingCertsInProgress$ = new Subject<void>();
          this.activateCertificatePulling(pendingCertReqId);
        }
      });
  }

  activateCertificatePulling(pendingCertificates: ICertificateData[]) {
    if (pendingCertificates.length > 0) {

      this.manageService.getCertificateStatus(pendingCertificates, this.pendingCertsInProgress$).pipe(
        takeUntil(this.pendingCertsInProgress$),
      ).subscribe((res: any) => {
        res?.data.filter((element) => element.status !== CertStatus.PENDING_HOLD && element.status !== CertStatus.PENDING_RESTORE)
          .map((element) => {
            const certificate = pendingCertificates.filter((e) => e.certReqId === element.certReqId)[0];
            if (certificate !== undefined && certificate.status !== element.status) {
              const msg = this.getToolTipMsg(element.status);
              this.operationResult(msg, true);
            }
          });
      });
    }
  }

  updateNumberOfDevices(devices: IMobileDevice[], certReqId: string) {
    this.store.dispatch(new ActionCertificatesUpdateDevicesState({ certReqId, newDevices: devices }));
  }

  private getData() {
    if (!this.isSubcomponent) {
      this.store.dispatch(new ActionCertificatesGet());
    }
  }

  private setPaginator() {
    if (this.dataSource) {
      this.dataSource!.paginator = this.paginator;
      this.dataSource!.sort = this.sort;
    }
  }

  private openConfirmDialog(
    title: string,
    question: string,
    // tslint:disable-next-line:ban-types
    confirmAction: Function,
    cert: ICertificateData
  ) {
    this.info.dismissAll();
    this._confirmDialog = this.dialog.open(
      ConfirmComponent,
      {
        data: {
          title: this.translateService.instant(this.dialogTexts + title),
          question: this.translateService.instant(this.dialogTexts + question,
            { alias: cert.alias ? cert.alias : '', certSerial: cert.serialNo }),
          confirmTxt: this.translateService.instant('common.yes'),
          cancelTxt: this.translateService.instant('common.no'),
          confirmAction,
        } as IConfirmData,
      });
  }
  private operationResult(msg: string, isSuccessful?: boolean, takeMsgAsIs = false) {
    msg = (takeMsgAsIs) ? msg : this.translateService.instant(this.resultTexts + msg);
    isSuccessful ? this.info.success(msg) : this.info.error(msg);
    if (this._confirmDialog) { this._confirmDialog.close(); }
  }

  openCertificateDetailsDialog(cert: ICertificateData) {
    const dialogRef = this.dialog.open(CertificateDetailsComponent, { data: cert });
    const sub = dialogRef.componentInstance.close.subscribe(() => {
      this.dialog.closeAll();
    });
  }

  getCertificateStatusText(value) {
    switch (value) {
      case 'active':
        return 'manageTexts.statusFilter.active';
      case 'inactive':
        return 'manageTexts.statusFilter.inactive';
      case 'all':
        return 'manageTexts.statusFilter.all';
      default:
        return 'manageTexts.statusFilter.active';
    }
  }

  getCertificateStatusFilter(statusType: string) {
    switch (statusType) {
      case 'active':
        return this.activeCertStatusFilter;
      case 'inactive':
        return this.inactiveCertStatusFilter;
      case 'all':
        return this.allCertStatusFilter;
      default:
        return this.activeCertStatusFilter;
    }
  }

  getToolTipMsg(certificateStatus: CertStatus): string {
    switch (certificateStatus) {
      case CertStatus.HOLDED: return 'holdSuccess';
      case CertStatus.VALID: return 'activateSuccess';
    }
  }

  loadFilteredCertificates() {
    this.store
      .select(selectorCertificate)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((certsFromStore) => {
        if (certsFromStore instanceof Array) {
          certsFromStore = certsFromStore.filter(this.getCertificateStatusFilter(this.certFilterStatus));
          this.onShowDevices.emit(null);
          this.resetFilter = '';
          this.setDataSource(certsFromStore);
        }
      });
  }

  getCertOwner(cert: ICertificateData) {
    if (cert.owner === undefined) {
      return '';
    }
    return cert.owner[this.lang];
  }

  getCommonName(cert: ICertificateData) {
    return cert.commonName;
  }
}
